import {
    ResponsiveModal,
    ResponsiveModalBody,
    ResponsiveModalContent,
    ResponsiveModalDescription,
    ResponsiveModalHeader,
    ResponsiveModalTitle,
} from '@/components/ui/responsive-modal';
import { Badge } from '@/components/ui/shadcn/badge';
import { Button } from '@/components/ui/shadcn/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/shadcn/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/shadcn/dialog';
import { Separator } from '@/components/ui/shadcn/separator';
import { useTranslations } from '@/hooks/useTranslations';
import { formatCurrency, formatDate } from '@/lib/utils';
import { Lead, LeadOffer, LeadOfferCostLine, LeadOfferFile, LeadOfferLine } from '@/types';
import type { OfferLineFormData } from '@/types/forms/leads';
import { format } from 'date-fns';
import { Download, File, X, ZoomIn } from 'lucide-react';
import { useState } from 'react';
import OfferLinesEditor from '../forms/OfferLinesEditor';

interface OfferDetailsModalProps {
    lead: Lead;
    offer: LeadOffer;
    isOpen: boolean;
    onClose: () => void;
}

type PreviewableType = 'image' | 'pdf';

export default function OfferDetailsModal({ lead, offer, isOpen, onClose }: OfferDetailsModalProps) {
    const { t } = useTranslations();
    const [previewFile, setPreviewFile] = useState<LeadOfferFile | null>(null);
    const [previewType, setPreviewType] = useState<PreviewableType | null>(null);

    // Debug: Log offer data to see what's available

    // Calculate costs from offer cost lines - try both camelCase and snake_case
    const costLines = offer.costLines || (offer as unknown as { cost_lines?: LeadOfferCostLine[] }).cost_lines || [];
    const offerLines = offer.lines || (offer as unknown as { offer_lines?: LeadOfferLine[] }).offer_lines || [];
    const readOnlyLines: OfferLineFormData[] = offerLines.map((line) => ({
        code: line.code ?? '',
        name: line.name,
        quantity: String(line.quantity ?? ''),
        unit: line.unit ?? '',
        discount: line.discount ?? '',
        unit_price: String(line.unit_price ?? ''),
        line_total: String(line.line_total ?? ''),
    }));
    const totalCosts =
        offer.total_costs !== undefined
            ? offer.total_costs
            : (offer as unknown as { totalCosts?: number }).totalCosts !== undefined
              ? (offer as unknown as { totalCosts?: number }).totalCosts
              : costLines.length > 0
                ? costLines.reduce((sum: number, cost: LeadOfferCostLine) => sum + cost.amount, 0)
                : 0;

    const profit = offer.sales_price - (totalCosts || 0);
    const profitPercentage = offer.sales_price > 0 ? (profit / offer.sales_price) * 100 : 0;

    const getPreviewType = (file: LeadOfferFile): PreviewableType | null => {
        const mime = file.mime_type?.toLowerCase() || '';
        if (mime.startsWith('image/')) return 'image';
        if (mime.includes('pdf')) return 'pdf';
        return null;
    };

    const handlePreview = (file: LeadOfferFile) => {
        const type = getPreviewType(file);
        if (type) {
            setPreviewFile(file);
            setPreviewType(type);
            return;
        }
        // For non-previewable files, download instead
        handleDownload(file);
    };

    const handleDownload = (file: LeadOfferFile) => {
        const url = `/leads/${lead.id}/offers/${offer.id}/files/${file.id}/download`;
        window.open(url, '_blank');
    };

    const closePreview = () => {
        setPreviewFile(null);
        setPreviewType(null);
    };

    const formatFileSize = (bytes: number): string => {
        if (!bytes) return '0 Bytes';
        const units = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(1024));
        const size = bytes / Math.pow(1024, i);
        return `${size.toFixed(size >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
    };

    const getStatusBadge = (status: LeadOffer['status']) => {
        switch (status) {
            case 'approved':
                return <Badge className="bg-success-subtle text-success-subtle-foreground">{t('leads.offers.status.approved', 'Approved')}</Badge>;
            case 'rejected':
                return <Badge className="bg-danger-subtle text-danger-subtle-foreground">{t('leads.offers.status.rejected', 'Rejected')}</Badge>;
            case 'pending':
            default:
                return <Badge className="bg-warning-subtle text-warning-subtle-foreground">{t('leads.offers.status.pending', 'Pending')}</Badge>;
        }
    };

    return (
        <>
            <ResponsiveModal open={isOpen} onOpenChange={onClose} variant="sheet">
                {/* Matches the create and edit modals: the offer lines table is eight
                    columns wide and forces horizontal scrolling in a narrower sheet. */}
                <ResponsiveModalContent size="2xl">
                    <ResponsiveModalHeader>
                        <ResponsiveModalTitle>{t('leads.offers.details.title', 'Offer Details')}</ResponsiveModalTitle>
                        <ResponsiveModalDescription>
                            {t('leads.offers.details.subtitle', 'View complete details and cost breakdown for this offer')}
                        </ResponsiveModalDescription>
                    </ResponsiveModalHeader>

                    <ResponsiveModalBody className="space-y-4 md:space-y-6">
                        {/* Offer Summary */}
                        <Card>
                            <CardHeader className="px-4 md:px-6">
                                <CardTitle className="flex items-center justify-between">
                                    <span>{t('leads.offers.details.summary', 'Offer Summary')}</span>
                                    {getStatusBadge(offer.status)}
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4 px-4 md:px-6">
                                <div className="grid grid-cols-2 gap-x-3 gap-y-3 md:gap-4">
                                    <div>
                                        <p className="text-muted-foreground mb-1 text-sm">{t('leads.offers.details.sales_price', 'Sales Price')}</p>
                                        <p className="text-base font-semibold md:text-lg">{formatCurrency(offer.sales_price)}</p>
                                    </div>
                                    <div>
                                        <p className="text-muted-foreground mb-1 text-sm">{t('leads.offers.details.total_costs', 'Total Costs')}</p>
                                        <p className="text-base font-semibold md:text-lg">{formatCurrency(totalCosts || 0)}</p>
                                    </div>
                                    <div>
                                        <p className="text-muted-foreground mb-1 text-sm">{t('leads.offers.details.profit', 'Profit')}</p>
                                        <p
                                            className={`text-base font-semibold md:text-lg ${profit >= 0 ? 'text-success-subtle-foreground' : 'text-destructive'}`}
                                        >
                                            {formatCurrency(profit)}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-muted-foreground mb-1 text-sm">
                                            {t('leads.offers.details.profit_margin', 'Profit Margin')}
                                        </p>
                                        <p
                                            className={`text-base font-semibold md:text-lg ${profitPercentage >= 0 ? 'text-success-subtle-foreground' : 'text-destructive'}`}
                                        >
                                            {profitPercentage.toFixed(2)}%
                                        </p>
                                    </div>
                                </div>

                                {offer.description && (
                                    <>
                                        <Separator />
                                        <div>
                                            <p className="text-muted-foreground mb-1 text-sm">
                                                {t('leads.offers.details.description', 'Description')}
                                            </p>
                                            <p className="text-sm whitespace-pre-wrap">{offer.description}</p>
                                        </div>
                                    </>
                                )}

                                <Separator />

                                <div className="text-muted-foreground text-sm">
                                    <p>
                                        {t('leads.offers.details.created', 'Created')} {format(new Date(offer.created_at), 'yyyy.MM.dd HH:mm')}
                                    </p>
                                    {offer.createdBy && (
                                        <p>
                                            {t('leads.offers.details.by', 'by')} {offer.createdBy.first_name} {offer.createdBy.last_name}
                                        </p>
                                    )}
                                    {offer.approved_at && offer.approvedBy && (
                                        <p className="mt-2">
                                            {t('leads.offers.details.approved_by', 'Approved by')} {offer.approvedBy.first_name}{' '}
                                            {offer.approvedBy.last_name} {t('leads.offers.details.on', 'on')}{' '}
                                            {format(new Date(offer.approved_at), 'yyyy.MM.dd HH:mm')}
                                        </p>
                                    )}
                                    {offer.rejected_at && offer.rejectedBy && (
                                        <p className="mt-2">
                                            {t('leads.offers.details.rejected_by', 'Rejected by')} {offer.rejectedBy.first_name}{' '}
                                            {offer.rejectedBy.last_name} {t('leads.offers.details.on', 'on')}{' '}
                                            {format(new Date(offer.rejected_at), 'yyyy.MM.dd HH:mm')}
                                        </p>
                                    )}
                                </div>
                            </CardContent>
                        </Card>

                        {/* Offer lines */}
                        {readOnlyLines.length > 0 && (
                            <Card>
                                <CardHeader className="px-4 md:px-6">
                                    <CardTitle>
                                        {t('leads.offer_lines.heading', 'Offer lines')} ({readOnlyLines.length})
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="px-4 md:px-6">
                                    <OfferLinesEditor lines={readOnlyLines} onChange={() => {}} disabled />
                                </CardContent>
                            </Card>
                        )}

                        {/* Cost Lines */}
                        <Card>
                            <CardHeader className="px-4 md:px-6">
                                <CardTitle>
                                    {t('leads.offers.details.cost_lines', 'Cost Lines')} ({costLines.length})
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="px-4 md:px-6">
                                {costLines.length > 0 ? (
                                    <div className="space-y-3">
                                        {costLines.map((costLine) => (
                                            <div key={costLine.id} className="bg-muted flex items-start justify-between gap-3 rounded-lg p-3">
                                                <div className="min-w-0 flex-1">
                                                    <div className="mb-1 flex items-center gap-2">
                                                        <p className="font-medium">{costLine.description}</p>
                                                    </div>
                                                    <p className="text-muted-foreground text-xs">
                                                        {formatDate(costLine.date_incurred)}
                                                        {costLine.createdBy && (
                                                            <>
                                                                {' '}
                                                                • {t('leads.offers.details.added_by', 'Added by')} {costLine.createdBy.first_name}{' '}
                                                                {costLine.createdBy.last_name}
                                                            </>
                                                        )}
                                                    </p>
                                                </div>
                                                <div className="shrink-0 text-right">
                                                    <p className="font-semibold">{formatCurrency(costLine.amount)}</p>
                                                </div>
                                            </div>
                                        ))}
                                        <Separator />
                                        <div className="flex items-center justify-between pt-2">
                                            <p className="font-semibold">{t('leads.offers.details.total_costs', 'Total Costs')}</p>
                                            <p className="text-base font-semibold md:text-lg">{formatCurrency(totalCosts || 0)}</p>
                                        </div>
                                    </div>
                                ) : (
                                    <p className="text-muted-foreground py-4 text-center text-sm">
                                        {t('leads.offers.details.no_cost_lines', 'No cost lines added to this offer.')}
                                    </p>
                                )}
                            </CardContent>
                        </Card>

                        {/* File Attachments */}
                        {offer.files && offer.files.length > 0 && (
                            <Card>
                                <CardHeader className="px-4 md:px-6">
                                    <CardTitle>
                                        {t('leads.offers.details.attachments', 'Attachments')} ({offer.files.length})
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="px-4 md:px-6">
                                    <div className="space-y-2">
                                        {offer.files.map((file) => {
                                            const isPreviewable = getPreviewType(file) !== null;

                                            return (
                                                <div key={file.id} className="bg-muted flex items-center justify-between rounded-lg p-3">
                                                    <div className="flex min-w-0 flex-1 items-center gap-3">
                                                        <File className="h-4 w-4 shrink-0" />
                                                        <div className="min-w-0 flex-1">
                                                            <span className="block truncate text-sm font-medium" title={file.file_name}>
                                                                {file.file_name}
                                                            </span>
                                                            <span className="text-muted-foreground text-xs">{formatFileSize(file.file_size)}</span>
                                                        </div>
                                                    </div>
                                                    <div className="flex shrink-0 items-center gap-1">
                                                        {isPreviewable && (
                                                            <Button
                                                                type="button"
                                                                variant="ghost"
                                                                size="icon"
                                                                onClick={() => handlePreview(file)}
                                                                className="h-8 w-8"
                                                            >
                                                                <ZoomIn className="h-4 w-4" />
                                                            </Button>
                                                        )}
                                                        <Button
                                                            type="button"
                                                            variant="ghost"
                                                            size="icon"
                                                            onClick={() => handleDownload(file)}
                                                            className="h-8 w-8"
                                                        >
                                                            <Download className="h-4 w-4" />
                                                        </Button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        {offer.rejection_reason && (
                            <Card>
                                <CardHeader className="px-4 md:px-6">
                                    <CardTitle>{t('leads.offers.details.rejection_reason', 'Rejection Reason')}</CardTitle>
                                </CardHeader>
                                <CardContent className="px-4 md:px-6">
                                    <p className="text-sm">{offer.rejection_reason}</p>
                                </CardContent>
                            </Card>
                        )}
                    </ResponsiveModalBody>
                </ResponsiveModalContent>
            </ResponsiveModal>

            {/* Preview sits in its own Dialog, a sibling of the modal rather than a
                child of it: as a bare fixed overlay it rendered outside the modal
                content while Radix still had the body inert. Deliberately a Dialog
                at every width — a bottom sheet is the wrong shape for a full-bleed
                image or document. */}
            <Dialog open={previewFile !== null && previewType !== null} onOpenChange={(open) => !open && closePreview()}>
                <DialogContent
                    showCloseButton={false}
                    className="h-[100dvh] max-w-[100vw] gap-0 overflow-hidden rounded-none p-0 sm:h-auto sm:max-h-[90vh] sm:max-w-5xl sm:rounded-xl"
                >
                    <DialogHeader className="sr-only">
                        <DialogTitle>{previewFile?.file_name ?? t('leads.offers.details.preview_title', 'File preview')}</DialogTitle>
                    </DialogHeader>

                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={closePreview}
                        aria-label={t('leads.offers.details.close_preview', 'Close preview')}
                        className="absolute top-3 right-3 z-10 h-9 w-9 rounded-full bg-black/60 text-white hover:bg-black/80"
                    >
                        <X className="h-4 w-4" />
                    </Button>

                    {previewFile && previewType === 'image' && (
                        <img
                            src={previewFile.file_url || `/storage/${previewFile.file_path}`}
                            alt={previewFile.file_name}
                            className="h-full max-h-[100dvh] w-full bg-black object-contain sm:max-h-[90vh]"
                        />
                    )}

                    {previewFile && previewType === 'pdf' && (
                        <>
                            {/* iOS Safari will not scroll a PDF in an iframe, so on a
                                phone the honest option is to hand it to the browser. */}
                            <div className="bg-muted flex h-full flex-col items-center justify-center gap-3 p-6 text-center md:hidden">
                                <File className="text-muted-foreground h-8 w-8" />
                                <p className="text-sm font-medium break-all">{previewFile.file_name}</p>
                                <Button type="button" onClick={() => handleDownload(previewFile)}>
                                    {t('leads.offers.details.open_in_new_tab', 'Open in a new tab')}
                                </Button>
                            </div>
                            <div className="hidden bg-black p-2 md:block">
                                <iframe
                                    src={`${previewFile.file_url || `/storage/${previewFile.file_path}`}#toolbar=1`}
                                    title={previewFile.file_name}
                                    className="h-[85vh] w-full border-0"
                                    scrolling="yes"
                                    allowFullScreen
                                />
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
}
